/*eslint-disable*/

import React, { useState, useMemo, useEffect } from "react";
import {
    MenuItem,
    Select,
    FormControl,
    SelectChangeEvent,
    Box,
} from "@mui/material";
import { FiCalendar } from "react-icons/fi";
import Grid from "@mui/material/Grid";
import DashboardUserLogins from "../../../components/Charts/DashboardUserLogins";
import UserKilometers from "../../../components/Charts/UserKilometers";
import UserMeetings from "../../../components/Charts/UserMeetings";

import FastAPIClient from "../../../client";
import config from "../../../config";
import GroupedGrid from "../../../components/Tables/GroupedGrid";
// import ClientVisitStats from "../../../components/Charts/ClientVisitStats";
import RevenueStats from "../../../components/Charts/RevenueStats";
import UserCoverage from "../../../components/Charts/UserCoverage";
import VolumeStats from "../../../components/Charts/VolumeStats";
import MetricStats from "../../../components/Charts/RevenueStatsNew";
import MeetingTypeChart from "../../../components/Charts/MeetingTypeChart";
import UserTypesChart from "../../../components/Charts/UserTypesChart";
import FilterGrid from "../../../components/Charts/FilterGrid";
// import { blueberryTwilightPalette } from "@mui/x-charts/colorPalettes";

// import CustomBarChart from "./CustomBarChart"; // Import the CustomBarChart

import {
    DateRange,
    DateRangePicker,
    LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { PickersShortcutsItem } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ClientStats from "../../../components/Charts/ClientStats";
import LeadStats from "../../../components/Charts/LeadStats";
import NBAChart from "../../../components/Charts/NBAChart";

const client = new FastAPIClient(config);

interface User {
    user_id: string;
    first_name: string;
    last_name: string;
    team_name?: string | null;
    warehouse_name?: string | null;
}

type MonthlyStat = {
    user_id: string;
    first_name: string;
    last_name: string;
    warehouse_name: string;
    year: number;
    month: number;
    monthly_revenue: number;
    monthly_volume: number;
    monthly_margin: number;
    distinct_clients: number;
    team_name: string;
    product_level_1?: string; // 👈 Add this
    client_level_1?: string; // 👈 Add this
};

const Dashboard: React.FC = () => {
    const [selectedUser, setSelectedUser] = useState<string>("all");
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>("all");
    const timePeriod = "last7Days";
    const [data, setData] = useState<User[]>([]);
    const [transactionData, setTransactionData] = useState<MonthlyStat[]>([]);

    useEffect(() => {
        fetchActivity();
    }, []);

    const fetchActivity = async () => {
        try {
            const response = await client.fetchTeamMembers();
            console.log(response.results);
            setData(response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("year"),
        dayjs(),
    ]);

    useEffect(() => {
        const [startDate, endDate] = dateRange;
        if (startDate && endDate) {
            const formattedStartDate = startDate.format("YYYY-MM-DD");
            const formattedEndDate = endDate.format("YYYY-MM-DD");
            fetchMonthlyStats(formattedStartDate, formattedEndDate);
        }
    }, [dateRange]);

    const fetchMonthlyStats = async (startDate: string, endDate: string) => {
        try {
            const response = await client.fetchMonthlyStats({
                startDate,
                endDate,
            });
            console.log("Monthly Stats", response.results);
            setTransactionData(response.results);
        } catch (error) {
            console.error("Error fetching monthly stats:", error);
        }
    };

    const filteredUsers =
        selectedWarehouse === "all"
            ? data
            : data.filter((user) => user.warehouse_name === selectedWarehouse);

    const uniqueUsers = useMemo(() => {
        return filteredUsers.map((user) => ({
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            warehouse_name: user.warehouse_name, // include warehouse info here
        }));
    }, [filteredUsers]);

    const uniqueWarehouses = useMemo(() => {
        const warehouses = data
            .map((user) => user.warehouse_name)
            .filter(Boolean);
        return Array.from(new Set(warehouses));
    }, [data]);

    const handleSelectChange =
        (setter: React.Dispatch<React.SetStateAction<string>>) =>
        (e: SelectChangeEvent<string>) => {
            setter(e.target.value);
        };

    // const [dateRange, setDateRange] = useState([
    //     dayjs().startOf("month"),
    //     dayjs().endOf("month"),
    // ]);

    const shortcuts: PickersShortcutsItem<DateRange<Dayjs>>[] = [
        {
            label: "Ce mois",
            getValue: () => [
                dayjs().startOf("month"),
                dayjs().isBefore(dayjs().endOf("month"))
                    ? dayjs()
                    : dayjs().endOf("month"),
            ],
        },
        {
            label: "Cette année",
            getValue: () => [
                dayjs().startOf("year"),
                dayjs().isBefore(dayjs().endOf("year"))
                    ? dayjs()
                    : dayjs().endOf("year"),
            ],
        },
    ];

    useEffect(() => {
        const [startDate, endDate] = dateRange;
        if (startDate && endDate) {
            console.log("Start:", startDate.format("YYYY-MM-DD"));
            console.log("End:", endDate.format("YYYY-MM-DD"));
        }
    }, [dateRange]);

    const handleUserSelectChange = (value: string) => {
        setSelectedUser(value);

        if (value !== "all") {
            // Find the selected user's warehouse and set it
            const selectedUserData = data.find(
                (user) => `${user.first_name} ${user.last_name}` === value
            );
            if (selectedUserData) {
                setSelectedWarehouse(selectedUserData.warehouse_name || "all");
            }
        } else {
            // If "all" is selected, reset warehouse to "all"
            setSelectedWarehouse("all");
        }
    };

    // Handle warehouse selection
    const handleWarehouseSelectChange = (value: string) => {
        setSelectedWarehouse(value);

        if (value !== "all") {
            // Find the first user in the selected warehouse and set it as selected user
            const firstUserInWarehouse = data.find(
                (user) => user.warehouse_name === value
            );
        } else {
            // If "all" is selected, reset user to "all"
            setSelectedUser("all");
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box sx={{ width: "100%" }}>
                <FilterGrid
                    selectedUser={selectedUser}
                    selectedWarehouse={selectedWarehouse}
                    dateRange={dateRange}
                    uniqueUsers={uniqueUsers}
                    uniqueWarehouses={uniqueWarehouses}
                    onUserChange={handleUserSelectChange} // Directly update the state
                    onWarehouseChange={handleWarehouseSelectChange} // Directly update the state
                    onDateRangeChange={setDateRange}
                />

                <Grid container spacing={2} columns={{ xs: 1, md: 2 }}>
                    <Grid container size={1} columns={{ xs: 1, md: 2 }}>
                        <Grid size={1}>
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "0.375rem",
                                }}
                            >
                                <UserTypesChart
                                    selectedUser={selectedUser}
                                    selectedTeam="all"
                                    selectedWarehouse={selectedWarehouse}
                                    dateRange={timePeriod}
                                />
                            </Box>
                        </Grid>
                        <Grid size={1}>
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "0.375rem",
                                }}
                            >
                                <MeetingTypeChart
                                    selectedUser={selectedUser}
                                    selectedTeam="all"
                                    selectedWarehouse={selectedWarehouse}
                                    dateRange={dateRange}
                                />
                            </Box>
                        </Grid>
                        <Grid size={1}>
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "0.375rem",
                                }}
                            >
                                <UserCoverage
                                    selectedUser={selectedUser}
                                    selectedWarehouse={selectedWarehouse}
                                    timePeriod={timePeriod}
                                    selectedTeam="all"
                                />
                            </Box>
                        </Grid>
                        <Grid size={1}>
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "0.375rem",
                                }}
                            >
                                <UserMeetings
                                    selectedUser={selectedUser}
                                    selectedWarehouse={selectedWarehouse}
                                    selectedTeam="all"
                                    dateRange={dateRange}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid size={1}>
                        <Box
                            sx={{
                                width: "100%",
                                border: "1px solid #E5E7EB",
                                borderRadius: "0.375rem",
                                mb: 2,
                            }}
                        >
                            <DashboardUserLogins
                                selectedUser={selectedUser}
                                selectedTeam="all"
                                selectedWarehouse={selectedWarehouse}
                                timePeriod={timePeriod}
                                dateRange={dateRange}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Dashboard;
